import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import RuleScreen from '../../shared/RuleScreen';
import SundargarhFilterBar from '../../shared/SundargarhFilterBar';
import StatTile from '../../shared/StatTile';
import DataTable from '../../shared/DataTable';
import Pagination, { normalizePagination } from '../../shared/Pagination';
import DetailDrawer from '../../shared/DetailDrawer';
import { buildParams } from '../../shared/apiParams';

const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const CATEGORY_LABEL = { all: 'All Records', exact: 'Exact (≥80%)', partial: 'Partial (50–79%)', low: 'Low (25–49%)', notexists: 'Not Exists (<25%)' };

const pctCell = (count, total) => (
  <>
    {count || 0}
    {total > 0 ? <span style={{ fontSize: '0.72rem', color: 'var(--muted)', marginLeft: 4 }}>({Math.round(((count || 0) / total) * 100)}%)</span> : null}
  </>
);

// Re-derived "Respondent not found in members": instead of matching against
// an external member-master roster (which doesn't exist for this Kobo
// form), matches the respondent's name against the SAME submission's own
// member_name1..9 roster.
export default function SundargarhMemberMatch({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, category: 'all', records: [], loading: false });
  const [memberPopup, setMemberPopup] = useState({ open: false, loading: false, respondentName: '', members: [], matchDetails: null });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/rules/member-match/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh member-match data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const totalSurveys = summary?.totalSurveys ?? sum('totalSurveys');
  const exact = summary?.exactMatches ?? sum('exactMatches');
  const partial = summary?.partialMatches ?? sum('partialMatches');
  const notExists = summary?.notExists ?? sum('notExists');

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" sub={summary ? undefined : 'Current page'} />
      <StatTile label="Exact Matches" value={exact} icon={CheckCircleIcon} accent="var(--success)" />
      <StatTile label="Partial Matches" value={partial} icon={WarningAmberIcon} accent="var(--warning)" />
      <StatTile label="Not Found" value={notExists} icon={ErrorIcon} accent="var(--danger)" />
    </>
  );

  const openDrilldown = async (row, category = 'all') => {
    setDrawer({ open: true, row, category, records: [], loading: true });
    try {
      const params = buildParams(applied, { extra: { groupBy: activeTab, groupValue: row.name, matchStatus: category, limit: 100 } });
      const res = await axios.get(`${API_URL}/sundargarh/rules/member-match/details?${params}`);
      setDrawer({ open: true, row, category, records: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching member-match detail records:', err);
      setDrawer({ open: true, row, category, records: [], loading: false });
    }
  };

  const countCell = (row, count, category) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, category); }}>
      {pctCell(count, row.totalSurveys)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'exactMatches', label: 'Exact (≥80%)', align: 'center', render: (r) => countCell(r, r.exactMatches, 'exact') },
    { key: 'partialMatches', label: 'Partial (50–79%)', align: 'center', render: (r) => countCell(r, r.partialMatches, 'partial') },
    { key: 'lowMatches', label: 'Low (25–49%)', align: 'center', render: (r) => countCell(r, r.lowMatches, 'low') },
    { key: 'notExists', label: 'Not Exists (<25%)', align: 'center', render: (r) => countCell(r, r.notExists, 'notexists') },
    {
      key: 'rate', label: 'Match Rate', align: 'center',
      render: (r) => {
        const rate = r.matchRate ?? 0;
        const cls = rate >= 80 ? 'badge-success' : rate >= 50 ? 'badge-warning' : 'badge-danger';
        return <span className={`badge ${cls}`}>{rate}%</span>;
      },
    },
  ];

  const openMemberPopup = async (record) => {
    const survey = record.survey || {};
    setMemberPopup({ open: true, loading: true, respondentName: survey.respondent_name, members: [], matchDetails: null });
    try {
      const res = await axios.get(`${API_URL}/sundargarh/rules/member-match/members?uuid=${encodeURIComponent(survey.uuid)}`);
      setMemberPopup({
        open: true, loading: false, respondentName: survey.respondent_name,
        members: res.data?.members || [], matchDetails: res.data?.matchDetails || null,
      });
    } catch (err) {
      console.error('Error fetching member details:', err);
      setMemberPopup((p) => ({ ...p, loading: false }));
    }
  };

  const memberPopupColumns = [
    { key: 'name', label: 'Member Name', render: (m) => <span className="cell-strong">{m.name || '—'}</span> },
    { key: 'relation', label: 'Relation', render: (m) => m.relation || '—' },
    { key: 'similarity', label: 'Name Match %', align: 'center', render: (m) => `${m.similarity ?? 0}%` },
  ];

  const drawerRecordColumns = [
    {
      key: 'respondent_name', label: 'Respondent Name',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openMemberPopup(r); }}>
          {r.survey?.respondent_name || '—'}
        </span>
      ),
    },
    { key: 'member_name', label: 'Matched Member Name', render: (r) => r.match?.matchedMemberName || '—' },
    { key: 'score', label: 'Match %', align: 'center', render: (r) => `${r.match?.score ?? 0}%` },
    {
      key: 'status', label: 'Match / Not Match', align: 'center',
      render: (r) => (
        <span className={`badge ${r.match?.status !== 'notexists' ? 'badge-success' : 'badge-danger'}`}>
          {r.match?.status !== 'notexists' ? `Match (${CATEGORY_LABEL[r.match?.status] || r.match?.status})` : 'Not Match'}
        </span>
      ),
    },
    { key: 'ulb', label: 'ULB', render: (r) => r.survey?.ulb || '—' },
    { key: 'ward', label: 'Ward', render: (r) => r.survey?.ward || '—' },
  ];

  return (
    <RuleScreen
      title={rule?.title || 'Respondent Not Found in Members'}
      subtitle="Fuzzy match of the respondent's name against their own household's member roster in the same submission."
      icon={PersonSearchIcon}
      backTo={rule?.category ? `/sundargarh-category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button key={t.key} className={`tab-btn${activeTab === t.key ? ' active' : ''}`} onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}>
                {t.label}
              </button>
            ))}
          </div>
          <SundargarhFilterBar onApply={(v) => { setApplied(v); setPage(1); }} onClear={() => { setApplied(EMPTY); setPage(1); }} loading={loading} />
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Statistics by {tab.label.replace('By ', '')}</h3>
        <Pagination pagination={pagination} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} unitLabel="groups" shown={rows.length} />
        <DataTable columns={columns} rows={rows} loading={loading} emptyMessage='No data found. Adjust filters and click "Apply Filters".' onRowClick={(r) => openDrilldown(r, 'all')} getRowKey={(r, i) => `${r.name}-${i}`} />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${CATEGORY_LABEL[drawer.category] || 'All Records'}` : ''}
        sections={
          drawer.row
            ? [{ title: 'Match Breakdown', items: [
                { label: 'Total Surveys', value: drawer.row.totalSurveys || 0 },
                { label: 'Exact', value: drawer.row.exactMatches || 0 },
                { label: 'Partial', value: drawer.row.partialMatches || 0 },
                { label: 'Low', value: drawer.row.lowMatches || 0 },
                { label: 'Not Exists', value: drawer.row.notExists || 0 },
              ] }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Flagged Records</h4>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '-0.4rem' }}>
            Click a respondent's name to see their household members and how each name matches.
          </p>
          <DataTable columns={drawerRecordColumns} rows={drawer.records} loading={drawer.loading} emptyMessage="No detail records available." getRowKey={(r, i) => r.survey?.uuid || i} />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={memberPopup.open}
        onClose={() => setMemberPopup((p) => ({ ...p, open: false }))}
        title={`Household Members: ${memberPopup.respondentName || ''}`}
        sections={
          memberPopup.matchDetails
            ? [{
                title: 'Match Summary',
                items: [
                  { label: 'Household Members', value: memberPopup.members.length },
                  { label: 'Best Matched Member', value: memberPopup.matchDetails.matchedMemberName || '—' },
                  { label: 'Matched Relation', value: memberPopup.matchDetails.matchedMemberRelation || '—' },
                  { label: 'Match Score', value: `${memberPopup.matchDetails.score ?? 0}%` },
                  { label: 'Status', value: CATEGORY_LABEL[memberPopup.matchDetails.status] || memberPopup.matchDetails.status || '—' },
                ],
              }]
            : []
        }
      >
        <div className="detail-section">
          <h4>All Household Members ({memberPopup.members.length})</h4>
          <DataTable columns={memberPopupColumns} rows={memberPopup.members} loading={memberPopup.loading} emptyMessage="No household members found for this respondent." getRowKey={(m, i) => `${m.name}-${i}`} />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
